import { CopyIcon, EditIcon, PlusIcon, TrashIcon } from '@persona-id/design-tokens/icons';
import produce from 'immer';
import styled from 'styled-components';

import ContextMenu, { Item } from 'dashboard/components/ContextMenu/ContextMenu';
import { ColumnProps, RelationSchemaType } from 'dashboard/components-lib/FieldSchemaEditor';
import { FieldSchemaType } from 'lib/fields';

const MenuIconContainer = styled.span<{ danger?: boolean }>`
  margin-right: 8px;
  width: 16px;
  display: inline-block;
  vertical-align: middle;
`;

interface FieldSchemaContextMenuProps {
  onAddNestedFieldSchema?: () => void;
  onEditFieldSchema?: () => void;
  onDeactivateFieldSchema?: () => void;
  onDeleteFieldSchema?: () => void;
}

const FieldSchemaContextMenu: React.FC<FieldSchemaContextMenuProps & ColumnProps> = ({
  field,
  prefix,
  appendChild,
  insertSibling,
  onEditFieldSchema,
  onDeactivateFieldSchema,
  onDeleteFieldSchema,
}) => {
  const icons: Record<string, React.ReactNode> = {
    'add-field': <PlusIcon />,
    'copy-field': <CopyIcon />,
    'edit-field': <EditIcon />,
    'deactivate-field': <TrashIcon />,
    'delete-field': <TrashIcon />,
  };

  const menuItems: Array<Item> = [];

  menuItems.push({
    id: 'edit-field',
    label: 'Edit Field',
    variant: 'basic',
    onClick: onEditFieldSchema,
  });

  if (
    field.type === FieldSchemaType.Hash ||
    (field.type === FieldSchemaType.Array &&
      field.config['item-schema']?.type === FieldSchemaType.Hash)
  ) {
    menuItems.push({
      id: 'add-field',
      label: 'Add Field',
      variant: 'basic',
      onClick: () => {
        appendChild(field);
      },
    });
  }

  menuItems.push({
    id: 'copy-field',
    label: 'Make a copy',
    variant: 'basic',
    onClick: () => {
      insertSibling(
        produce(field, (draftSchema) => {
          draftSchema.key = `${field.key}_copy`;
          draftSchema.label = `${field.label} Copy`;
          draftSchema.persisted = false;

          if (
            draftSchema.type === RelationSchemaType.RelationSchema &&
            draftSchema['inverse-key']
          ) {
            draftSchema['inverse-key'] = `${draftSchema['inverse-key']}_copy`;
            draftSchema['inverse-label'] = `${draftSchema['inverse-label']} Copy`;
          }
        }),
      );
    },
  });

  if (onDeactivateFieldSchema != null) {
    menuItems.push({
      id: 'deactivate-field',
      label: 'Deactivate',
      variant: 'danger',
      onClick: onDeactivateFieldSchema,
    });
  }

  if (onDeleteFieldSchema != null) {
    menuItems.push({
      id: 'delete-field',
      label: 'Delete',
      variant: 'danger',
      onClick: onDeleteFieldSchema,
    });
  }

  return (
    <ContextMenu
      size='sm'
      variant='inverse'
      items={menuItems}
      renderItem={(item: Item) => (
        <ContextMenu.Item
          id={`field-schemas-table__row--${prefix}__${field.key}`}
          key={`field-schemas-table__row--${prefix}__${field.key}`}
          onClick={(e) => {
            // Needed to avoid triggering row click below
            e.stopPropagation();
            e.preventDefault();
            item.onClick?.();
          }}
          variant={item.variant}
        >
          <MenuIconContainer>{icons[item.id]}</MenuIconContainer>
          {item.label}
        </ContextMenu.Item>
      )}
    />
  );
};

export default FieldSchemaContextMenu;
